/**
 * ✅ Migrated to authStore_v1 - 2026-01-07
 * 
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login page if user is not authenticated.
 *
 * Exception: Allows access to root path if secret_key parameter is present
 */

import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useIsAuthenticated } from "@/stores/authStore_v1";

interface ProtectedRouteProps {
  redirectTo?: string;
  children?: React.ReactNode;
  allowSecretKey?: boolean; // Allow bypass with secret_key parameter
}

/**
 * Protected Route - Requires Authentication
 *
 * @example
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 */
export function ProtectedRoute({
  redirectTo: _redirectTo = "/auth/login",
  children,
  allowSecretKey = false,
}: ProtectedRouteProps) {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  // Check if secret_key parameter is present (for OAuth callback)
  const searchParams = new URLSearchParams(location.search);
  const hasSecretKey = searchParams.has("secret_key");

  // If secret_key is present and allowed, bypass authentication
  if (allowSecretKey && hasSecretKey) {
    return children ? <>{children}</> : <Outlet />;
  }

  // Redirect to login if not authenticated
  // Note: Loading is handled by App.tsx AuthHandler
  if (!isAuthenticated) {
    console.log(
      "🔒 ProtectedRoute: User not authenticated, redirecting to",
      _redirectTo
    );
    return <Navigate to={_redirectTo} replace />;
  }

  // Render children or outlet
  return children ? <>{children}</> : <Outlet />;
}

export default ProtectedRoute;
